import React from 'react';
import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { alpha } from '@mui/material/styles';
import CircleIcon from '@mui/icons-material/Circle';
import DashboardOutlinedIcon from '@mui/icons-material/DashboardOutlined';
import SensorsOutlinedIcon from '@mui/icons-material/SensorsOutlined';
import NotificationsNoneOutlinedIcon from '@mui/icons-material/NotificationsNoneOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import ChevronRightOutlinedIcon from '@mui/icons-material/ChevronRightOutlined';
import { Link, useLocation } from 'react-router-dom';
import {
  factoryName,
  lineName,
  operatorName,
  sensorFrequencyLabel,
  statusChips,
  uiRefreshLabel,
} from './mockData';

function SidebarItem({ icon, label, to, active }) {
  return (
    <ListItemButton
      component={Link}
      to={to}
      sx={{
        borderRadius: 2.5,
        mb: 0.5,
        bgcolor: active ? 'rgba(255,255,255,0.08)' : 'transparent',
        borderLeft: active ? '3px solid #3b82f6' : '3px solid transparent',
      }}
    >
      <ListItemIcon sx={{ color: active ? '#f8fafc' : 'rgba(255,255,255,0.65)', minWidth: 40 }}>{icon}</ListItemIcon>
      <ListItemText
        primary={label}
        primaryTypographyProps={{
          color: active ? '#f8fafc' : 'rgba(255,255,255,0.76)',
          fontWeight: active ? 700 : 500,
        }}
      />
      {active ? <ChevronRightOutlinedIcon sx={{ color: 'rgba(255,255,255,0.42)' }} /> : null}
    </ListItemButton>
  );
}

export default function MachineAdminShell({ pageTitle, pageSubtitle, children, pageActions }) {
  const location = useLocation();
  const isOverview = location.pathname === '/machine-admin';
  const isDetail = location.pathname.startsWith('/machine-admin/machines/');

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#0a1015', color: '#f8fafc' }}>
      <Box sx={{ position: 'sticky', top: 0, zIndex: 10, backdropFilter: 'blur(14px)', bgcolor: 'rgba(10,16,21,0.84)', borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
        <Box sx={{ px: { xs: 2, md: 3 }, py: 1.75, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Avatar sx={{ bgcolor: '#111b24', color: '#93c5fd', width: 40, height: 40 }}>
              <VisibilityOutlinedIcon />
            </Avatar>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 800 }}>
                Makinenin Gözü
              </Typography>
              <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.6)' }}>
                {factoryName} · {lineName} · Makine Yönetimi
              </Typography>
            </Box>
          </Stack>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: 'wrap' }}>
            <Chip
              icon={<CircleIcon sx={{ color: '#4ade80 !important', fontSize: 12 }} />}
              label={sensorFrequencyLabel}
              sx={{ bgcolor: 'rgba(255,255,255,0.06)', color: '#e2e8f0' }}
            />
            <Chip label={uiRefreshLabel} sx={{ bgcolor: 'rgba(255,255,255,0.06)', color: '#e2e8f0' }} />
            <IconButton sx={{ color: 'rgba(255,255,255,0.82)' }}>
              <NotificationsNoneOutlinedIcon />
            </IconButton>
            <Chip avatar={<Avatar sx={{ bgcolor: '#1d4ed8', width: 24, height: 24, fontSize: 12 }}>A</Avatar>} label={operatorName} sx={{ bgcolor: 'rgba(255,255,255,0.06)', color: '#fff', pl: 0.5 }} />
          </Stack>
        </Box>
      </Box>
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '270px 1fr' }, minHeight: 'calc(100vh - 74px)' }}>
        <Box component="aside" sx={{ borderRight: { lg: '1px solid rgba(255,255,255,0.08)' }, p: 2, display: { xs: 'none', lg: 'block' }, bgcolor: 'rgba(12,18,24,0.92)' }}>
          <Typography variant="overline" sx={{ color: 'rgba(255,255,255,0.5)', letterSpacing: 1.2 }}>
            Fabrika Görünümü
          </Typography>
          <List sx={{ mt: 1 }}>
            <SidebarItem icon={<DashboardOutlinedIcon />} label="Genel İzleme" to="/machine-admin" active={isOverview} />
            <SidebarItem icon={<SensorsOutlinedIcon />} label="Makine Detayı" to="/machine-admin/machines/kirma-makinasi-3" active={isDetail} />
          </List>
          <Divider sx={{ borderColor: 'rgba(255,255,255,0.08)', my: 2 }} />
          <Typography variant="overline" sx={{ color: 'rgba(255,255,255,0.5)', letterSpacing: 1.2 }}>
            Durum Özeti
          </Typography>
          <Stack spacing={1} sx={{ mt: 1.5 }}>
            {statusChips.map((item) => (
              <Chip
                key={item.label}
                icon={<CircleIcon sx={{ color: `${item.color} !important`, fontSize: 12 }} />}
                label={item.label}
                sx={{
                  justifyContent: 'flex-start',
                  bgcolor: alpha(item.color, 0.14),
                  color: '#fff',
                  border: `1px solid ${alpha(item.color, 0.3)}`,
                  fontWeight: 700,
                }}
              />
            ))}
          </Stack>
        </Box>
        <Box sx={{ p: { xs: 2, md: 3 } }}>
          <Stack spacing={3}>
            <Stack direction={{ xs: 'column', xl: 'row' }} spacing={2} justifyContent="space-between" alignItems={{ xs: 'flex-start', xl: 'center' }}>
              <Box>
                <Typography variant="h4" sx={{ fontWeight: 900, mb: 0.75 }}>
                  {pageTitle}
                </Typography>
                <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.64)' }}>
                  {pageSubtitle}
                </Typography>
              </Box>
              {pageActions || null}
            </Stack>
            {children}
          </Stack>
        </Box>
      </Box>
    </Box>
  );
}
